import { Helmet } from "react-helmet-async";
import { useParams, Navigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageHero } from "@/components/shared/PageHero";
import { CTASection } from "@/components/shared/CTASection";
import { CheckCircle, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getProjectBySlug, projects } from "@/data/projects";

// Default image for projects
import defaultProjectImage from "@/assets/hero-lawn-striped.jpg";

const ProjectDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  
  // Try to fetch from database first
  const { data: dbProject, isLoading } = useQuery({
    queryKey: ['project', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });
  
  // Check static projects as fallback
  const staticProject = slug ? getProjectBySlug(slug) : undefined;
  
  // Use database project if found, otherwise use static
  const project = dbProject || staticProject;
  const isDatabaseProject = !!dbProject;

  if (isLoading) {
    return (
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-secondary" />
        </div>
      </main>
    );
  }

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  // Get navigation for static projects
  const currentIndex = projects.findIndex((p) => p.slug === slug);
  const prevProject = currentIndex > 0 ? projects[currentIndex - 1] : null;
  const nextProject = currentIndex < projects.length - 1 ? projects[currentIndex + 1] : null;

  // Helper to parse markdown links in description
  const parseDescription = (text: string) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;
    let partIndex = 0;

    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      
      const linkText = match[1];
      let linkUrl = match[2];
      
      // Check if it's an internal link
      const isInternalPath = linkUrl.startsWith('/');
      
      if (isInternalPath) {
        parts.push(
          <Link 
            key={`link-${partIndex++}`} 
            to={linkUrl} 
            className="text-secondary hover:underline font-medium"
          >
            {linkText}
          </Link>
        );
      } else {
        parts.push(
          <a 
            key={`link-${partIndex++}`} 
            href={linkUrl} 
            className="text-secondary hover:underline font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >
            {linkText}
          </a>
        );
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : [text];
  };

  return (
    <>
      <Helmet>
        <title>{project.title} | Riekels Lawn Care</title>
        <meta
          name="description"
          content={`${project.description} Lawn care project in ${project.location} by Riekels Lawn Care & Landscaping.`}
        />
      </Helmet>
      <main>
        <PageHero
          badge={project.location}
          title={project.title}
          subtitle=""
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Link to="/projects">
                <Button variant="ghost" className="mb-8">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to All Projects
                </Button>
              </Link>

              {/* Project Image */}
              {(isDatabaseProject || project.image_url) && (
                <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={project.image_url || defaultProjectImage} 
                    alt={project.title} 
                    className="w-full h-auto object-cover aspect-video"
                  />
                </div>
              )}

              {/* Project Description */}
              <div className="feature-card mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground mb-4">
                  About This Project
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  {parseDescription(project.description)}
                </p>
              </div>

              <div className="feature-card">
                <h2 className="text-2xl font-heading font-bold text-foreground mb-6">
                  Project Highlights
                </h2>
                <ul className="space-y-4">
                  {project.highlights.map((highlight, i) => (
                    <li key={i} className="flex items-center gap-3 text-muted-foreground">
                      <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0" />
                      <span className="text-lg">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Navigation for static projects */}
              {!isDatabaseProject && (
                <div className="flex justify-between mt-8">
                  {prevProject ? (
                    <Link to={`/projects/${prevProject.slug}`}>
                      <Button variant="outline">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        {prevProject.title}
                      </Button>
                    </Link>
                  ) : (
                    <div />
                  )}
                  {nextProject && (
                    <Link to={`/projects/${nextProject.slug}`}>
                      <Button variant="outline">
                        {nextProject.title}
                        <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        <CTASection />
      </main>
    </>
  );
};

export default ProjectDetail;
